import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { HiEye, HiEyeOff, HiExclamationCircle } from 'react-icons/hi';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PASSWORD_REGEX = /^(?=.*[0-9])(?=.*[a-zA-Z])(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]).{9,20}$/;
const ADMIN_CODE = 'ddiring2025';

const AuthRegisterPage = () => {
    const navigate = useNavigate();
    const serviceName = process.env.REACT_APP_NAME || '서비스';

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [adminCode, setAdminCode] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [emailTouched, setEmailTouched] = useState(false);
    const [passwordTouched, setPasswordTouched] = useState(false);
    const [adminCodeTouched, setAdminCodeTouched] = useState(false);

    const isEmailValid = EMAIL_REGEX.test(email);
    const isPasswordValid = PASSWORD_REGEX.test(password);
    const isAdminCodeValid = adminCode === ADMIN_CODE;

    const showEmailError = emailTouched && email !== '' && !isEmailValid;
    const showPasswordError = passwordTouched && password !== '' && !isPasswordValid;
    const showAdminCodeError = adminCodeTouched && adminCode !== '' && !isAdminCodeValid;

    const isActive = isEmailValid && isPasswordValid && isAdminCodeValid;

    const handleSubmit = () => {
        if (!isActive) return;
        navigate('/auth/register/terms', { state: { email, password } });
    };

    return (
        <div style={{ width: '100dvw', height: '100dvh', display: 'flex' }}>
            <BackgroundLayout>
                <FormWrapper>
                    <Title>이메일로 회원가입</Title>

                    <FieldGroup>
                        <Label>이메일</Label>
                        <Input
                            type="email"
                            placeholder="이메일을 입력해주세요"
                            value={email}
                            $error={showEmailError}
                            onChange={(e) => setEmail(e.target.value)}
                            onBlur={() => setEmailTouched(true)}
                        />
                        {showEmailError && (
                            <ErrorMessage>
                                <HiExclamationCircle size={16} color="#EF4444" />
                                올바른 이메일 형식이 아닙니다.
                            </ErrorMessage>
                        )}
                    </FieldGroup>

                    <FieldGroup>
                        <Label>비밀번호</Label>
                        <PasswordWrapper>
                            <Input
                                as="input"
                                type={showPassword ? 'text' : 'password'}
                                placeholder="비밀번호를 입력해주세요"
                                value={password}
                                $error={showPasswordError}
                                onChange={(e) => setPassword(e.target.value)}
                                onBlur={() => setPasswordTouched(true)}
                                style={{ paddingRight: '48px' }}
                            />
                            <EyeButton type="button" onClick={() => setShowPassword((v) => !v)}>
                                {showPassword ? <HiEye size={20} color="#AAAAAA" /> : <HiEyeOff size={20} color="#AAAAAA" />}
                            </EyeButton>
                        </PasswordWrapper>
                        {showPasswordError ? (
                            <ErrorMessage>
                                <HiExclamationCircle size={16} color="#EF4444" />
                                숫자, 영문자, 특수문자 포함 9 - 20자로 입력해주세요.
                            </ErrorMessage>
                        ) : (
                            <Hint>숫자, 영문자, 특수문자 포함 9 - 20자로 입력해주세요.</Hint>
                        )}
                    </FieldGroup>

                    <FieldGroup>
                        <Label>관리자 코드</Label>
                        <Input
                            type="password"
                            placeholder="관리자 코드를 입력해주세요"
                            value={adminCode}
                            $error={showAdminCodeError}
                            onChange={(e) => setAdminCode(e.target.value)}
                            onBlur={() => setAdminCodeTouched(true)}
                        />
                        {showAdminCodeError && (
                            <ErrorMessage>
                                <HiExclamationCircle size={16} color="#EF4444" />
                                올바른 관리자 코드가 아닙니다.
                            </ErrorMessage>
                        )}
                    </FieldGroup>

                    <SubmitButton $active={isActive} onClick={handleSubmit}>
                        다음
                    </SubmitButton>

                    <BottomText>
                        이미 {serviceName} 회원이신가요?&nbsp;
                        <LoginLink onClick={() => navigate('/auth/login/email')}>로그인</LoginLink>
                    </BottomText>
                </FormWrapper>
            </BackgroundLayout>
        </div>
    );
};

const BackgroundLayout = styled.div`
    flex: 1;
    background: #FFFFFF;
    display: flex;
    align-items: center;
    justify-content: center;

    @media ${({ theme }) => theme.tablet} {
        background: #FFFFFF;
        padding: 40px;
    }

    @media ${({ theme }) => theme.mobile} {
        background: #FFFFFF;
        padding: 20px;
    }
`;

const FormWrapper = styled.div`
    width: 480px;
    padding: 40px;
    border-radius: 16px;
    // border: 1px solid #E5E8EB;
    // box-shadow: rgba(100, 100, 111, 0.2) 0px 7px 29px 0px;
    background: #FFFFFF;
    display: flex;
    flex-direction: column;
    align-items: stretch;

    @media ${({ theme }) => theme.tablet} {
        padding: 0px;
        box-shadow: none;
        border: none;
        width: 100%;
    }
`;

const Title = styled.h1`
    font-family: Pretendard;
    font-size: 24px;
    font-weight: 700;
    color: #232323;
    text-align: center;
    margin: 0 0 32px;
    letter-spacing: -0.5px;
`;

const FieldGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 20px;
`;

const Label = styled.span`
    font-family: Pretendard;
    font-size: 14px;
    font-weight: 700;
    color: #232323;
    letter-spacing: -0.3px;
`;

const Input = styled.input`
    height: 56px;
    padding: 0 16px;
    border-radius: 12px;
    border: 1px solid ${({ $error }) => ($error ? '#EF4444' : '#E5E7EB')};
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 400;
    color: #232323;
    outline: none;
    box-sizing: border-box;
    width: 100%;
    background: #FFFFFF;
    transition: border-color 0.15s;

    &::placeholder {
        color: #AAAAAA;
    }

    &:focus {
        border-color: ${({ $error }) => ($error ? '#EF4444' : '#3D3D57')};
    }
`;

const PasswordWrapper = styled.div`
    position: relative;
    display: flex;
    align-items: center;
`;

const EyeButton = styled.button`
    position: absolute;
    right: 14px;
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    display: flex;
    align-items: center;
`;

const Hint = styled.span`
    font-family: Pretendard;
    font-size: 12px;
    font-weight: 400;
    color: #AAAAAA;
    letter-spacing: -0.3px;
`;

const ErrorMessage = styled.span`
    display: flex;
    align-items: center;
    gap: 4px;
    font-family: Pretendard;
    font-size: 12px;
    font-weight: 400;
    color: #EF4444;
    letter-spacing: -0.3px;
`;

const SubmitButton = styled.button`
    height: 56px;
    border-radius: 12px;
    border: none;
    background: ${({ $active }) => ($active ? '#3D3D57' : '#D1D5DB')};
    color: #FFFFFF;
    font-family: Pretendard;
    font-size: 16px;
    font-weight: 700;
    letter-spacing: -0.6px;
    cursor: ${({ $active }) => ($active ? 'pointer' : 'default')};
    margin-top: 8px;
    transition: background 0.15s;
`;

const BottomText = styled.p`
    margin: 24px 0 0;
    text-align: center;
    font-family: Pretendard;
    font-size: 14px;
    font-weight: 400;
    color: #999999;
    letter-spacing: -0.3px;
`;

const LoginLink = styled.span`
    font-family: Pretendard;
    font-size: 14px;
    font-weight: 700;
    color: #3D3D57;
    cursor: pointer;
    text-decoration: underline;
`;

export default AuthRegisterPage;
